/**
 * Color palette constants for the color swatch picker
 * Organized by category with 120+ curated colors
 */

export interface SwatchColor {
  label: string;
  hex: string;
  category: 'recent' | 'neutral' | 'core' | 'accent' | 'pastel' | 'custom';
  textPreviewClass?: string;
}

// Neutral colors - grayscale ramp
export const NEUTRAL_COLORS: SwatchColor[] = [
  { label: 'Black', hex: '#000000', category: 'neutral' },
  { label: 'Gray 900', hex: '#111827', category: 'neutral' },
  { label: 'Gray 800', hex: '#1f2937', category: 'neutral' },
  { label: 'Gray 700', hex: '#374151', category: 'neutral' },
  { label: 'Gray 600', hex: '#4b5563', category: 'neutral' },
  { label: 'Gray 500', hex: '#6b7280', category: 'neutral' },
  { label: 'Gray 400', hex: '#9ca3af', category: 'neutral' },
  { label: 'Gray 300', hex: '#d1d5db', category: 'neutral' },
  { label: 'Gray 200', hex: '#e5e7eb', category: 'neutral' },
  { label: 'Gray 100', hex: '#f3f4f6', category: 'neutral' },
  { label: 'Gray 50', hex: '#f9fafb', category: 'neutral' },
  { label: 'White', hex: '#ffffff', category: 'neutral' },
];

// Core brand/primary colors
export const CORE_COLORS: SwatchColor[] = [
  { label: 'Emerald 600', hex: '#059669', category: 'core' },
  { label: 'Emerald 500', hex: '#10b981', category: 'core' },
  { label: 'Emerald 400', hex: '#34d399', category: 'core' },
  { label: 'Blue 700', hex: '#1d4ed8', category: 'core' },
  { label: 'Blue 600', hex: '#2563eb', category: 'core' },
  { label: 'Blue 500', hex: '#3b82f6', category: 'core' },
  { label: 'Blue 400', hex: '#60a5fa', category: 'core' },
  { label: 'Cyan 600', hex: '#0891b2', category: 'core' },
  { label: 'Cyan 500', hex: '#06b6d4', category: 'core' },
  { label: 'Purple 600', hex: '#9333ea', category: 'core' },
  { label: 'Purple 500', hex: '#a855f7', category: 'core' },
  { label: 'Pink 600', hex: '#db2777', category: 'core' },
];

// Accent colors - full spectrum, vibrant
export const ACCENT_COLORS: SwatchColor[] = [
  // Reds
  { label: 'Red 900', hex: '#7f1d1d', category: 'accent' },
  { label: 'Red 800', hex: '#991b1b', category: 'accent' },
  { label: 'Red 700', hex: '#b91c1c', category: 'accent' },
  { label: 'Red 600', hex: '#dc2626', category: 'accent' },
  { label: 'Red 500', hex: '#ef4444', category: 'accent' },
  { label: 'Red 400', hex: '#f87171', category: 'accent' },
  { label: 'Red 300', hex: '#fca5a5', category: 'accent' },
  
  // Oranges
  { label: 'Orange 900', hex: '#7c2d12', category: 'accent' },
  { label: 'Orange 800', hex: '#9a3412', category: 'accent' },
  { label: 'Orange 700', hex: '#c2410c', category: 'accent' },
  { label: 'Orange 600', hex: '#ea580c', category: 'accent' },
  { label: 'Orange 500', hex: '#f97316', category: 'accent' },
  { label: 'Orange 400', hex: '#fb923c', category: 'accent' },
  { label: 'Orange 300', hex: '#fdba74', category: 'accent' },
  
  // Yellows
  { label: 'Yellow 900', hex: '#713f12', category: 'accent' },
  { label: 'Yellow 800', hex: '#854d0e', category: 'accent' },
  { label: 'Yellow 700', hex: '#a16207', category: 'accent' },
  { label: 'Yellow 600', hex: '#ca8a04', category: 'accent' },
  { label: 'Yellow 500', hex: '#eab308', category: 'accent' },
  { label: 'Yellow 400', hex: '#facc15', category: 'accent' },
  { label: 'Yellow 300', hex: '#fde047', category: 'accent' },
  
  // Limes/Greens
  { label: 'Lime 700', hex: '#4d7c0f', category: 'accent' },
  { label: 'Lime 600', hex: '#65a30d', category: 'accent' },
  { label: 'Lime 500', hex: '#84cc16', category: 'accent' },
  { label: 'Lime 400', hex: '#a3e635', category: 'accent' },
  { label: 'Green 900', hex: '#14532d', category: 'accent' },
  { label: 'Green 800', hex: '#166534', category: 'accent' },
  { label: 'Green 700', hex: '#15803d', category: 'accent' },
  { label: 'Green 600', hex: '#16a34a', category: 'accent' },
  { label: 'Green 500', hex: '#22c55e', category: 'accent' },
  { label: 'Green 400', hex: '#4ade80', category: 'accent' },
  { label: 'Green 300', hex: '#86efac', category: 'accent' },
  
  // Teals/Cyans
  { label: 'Teal 900', hex: '#134e4a', category: 'accent' },
  { label: 'Teal 800', hex: '#115e59', category: 'accent' },
  { label: 'Teal 700', hex: '#0f766e', category: 'accent' },
  { label: 'Teal 600', hex: '#0d9488', category: 'accent' },
  { label: 'Teal 500', hex: '#14b8a6', category: 'accent' },
  { label: 'Teal 400', hex: '#2dd4bf', category: 'accent' },
  { label: 'Teal 300', hex: '#5eead4', category: 'accent' },
  { label: 'Cyan 700', hex: '#0e7490', category: 'accent' },
  { label: 'Cyan 400', hex: '#22d3ee', category: 'accent' },
  { label: 'Cyan 300', hex: '#67e8f9', category: 'accent' },
  
  // Blues
  { label: 'Sky 700', hex: '#0369a1', category: 'accent' },
  { label: 'Sky 600', hex: '#0284c7', category: 'accent' },
  { label: 'Sky 500', hex: '#0ea5e9', category: 'accent' },
  { label: 'Sky 400', hex: '#38bdf8', category: 'accent' },
  { label: 'Sky 300', hex: '#7dd3fc', category: 'accent' },
  
  // Indigos
  { label: 'Indigo 900', hex: '#312e81', category: 'accent' },
  { label: 'Indigo 800', hex: '#3730a3', category: 'accent' },
  { label: 'Indigo 700', hex: '#4338ca', category: 'accent' },
  { label: 'Indigo 600', hex: '#4f46e5', category: 'accent' },
  { label: 'Indigo 500', hex: '#6366f1', category: 'accent' },
  { label: 'Indigo 400', hex: '#818cf8', category: 'accent' },
  { label: 'Indigo 300', hex: '#a5b4fc', category: 'accent' },
  
  // Violets/Purples
  { label: 'Violet 900', hex: '#4c1d95', category: 'accent' },
  { label: 'Violet 800', hex: '#5b21b6', category: 'accent' },
  { label: 'Violet 700', hex: '#6d28d9', category: 'accent' },
  { label: 'Violet 600', hex: '#7c3aed', category: 'accent' },
  { label: 'Violet 500', hex: '#8b5cf6', category: 'accent' },
  { label: 'Violet 400', hex: '#a78bfa', category: 'accent' },
  { label: 'Violet 300', hex: '#c4b5fd', category: 'accent' },
  
  // Fuchsias/Pinks
  { label: 'Fuchsia 900', hex: '#701a75', category: 'accent' },
  { label: 'Fuchsia 800', hex: '#86198f', category: 'accent' },
  { label: 'Fuchsia 700', hex: '#a21caf', category: 'accent' },
  { label: 'Fuchsia 600', hex: '#c026d3', category: 'accent' },
  { label: 'Fuchsia 500', hex: '#d946ef', category: 'accent' },
  { label: 'Fuchsia 400', hex: '#e879f9', category: 'accent' },
  { label: 'Fuchsia 300', hex: '#f0abfc', category: 'accent' },
  { label: 'Pink 800', hex: '#9f1239', category: 'accent' },
  { label: 'Pink 700', hex: '#be123c', category: 'accent' },
  { label: 'Pink 500', hex: '#ec4899', category: 'accent' },
  { label: 'Pink 400', hex: '#f472b6', category: 'accent' },
  { label: 'Pink 300', hex: '#f9a8d4', category: 'accent' },
  
  // Roses
  { label: 'Rose 800', hex: '#9f1239', category: 'accent' },
  { label: 'Rose 700', hex: '#be123c', category: 'accent' },
  { label: 'Rose 600', hex: '#e11d48', category: 'accent' },
  { label: 'Rose 500', hex: '#f43f5e', category: 'accent' },
  { label: 'Rose 400', hex: '#fb7185', category: 'accent' },
  { label: 'Rose 300', hex: '#fda4af', category: 'accent' },
];

// Pastel colors - soft, muted tones
export const PASTEL_COLORS: SwatchColor[] = [
  { label: 'Pastel Red', hex: '#ffcccb', category: 'pastel' },
  { label: 'Pastel Coral', hex: '#ffd4cc', category: 'pastel' },
  { label: 'Pastel Orange', hex: '#ffdab9', category: 'pastel' },
  { label: 'Pastel Peach', hex: '#ffe4b5', category: 'pastel' },
  { label: 'Pastel Yellow', hex: '#fff9c4', category: 'pastel' },
  { label: 'Pastel Lime', hex: '#f0f4c3', category: 'pastel' },
  { label: 'Pastel Green', hex: '#c8e6c9', category: 'pastel' },
  { label: 'Pastel Mint', hex: '#b2dfdb', category: 'pastel' },
  { label: 'Pastel Teal', hex: '#b2ebf2', category: 'pastel' },
  { label: 'Pastel Cyan', hex: '#b3e5fc', category: 'pastel' },
  { label: 'Pastel Sky', hex: '#bbdefb', category: 'pastel' },
  { label: 'Pastel Blue', hex: '#c5cae9', category: 'pastel' },
  { label: 'Pastel Indigo', hex: '#d1c4e9', category: 'pastel' },
  { label: 'Pastel Purple', hex: '#e1bee7', category: 'pastel' },
  { label: 'Pastel Pink', hex: '#f8bbd0', category: 'pastel' },
  { label: 'Pastel Rose', hex: '#ffccbc', category: 'pastel' },
];

// Combine all colors into a single palette
export const ALL_COLORS: SwatchColor[] = [
  ...NEUTRAL_COLORS,
  ...CORE_COLORS,
  ...ACCENT_COLORS,
  ...PASTEL_COLORS,
];

// Helper functions
export function isValidHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!isValidHex(hex)) return null;
  
  const result = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function getContrastTextColor(hex: string): 'light' | 'dark' {
  const rgb = hexToRgb(hex);
  if (!rgb) return 'dark';
  
  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? 'dark' : 'light';
}
